import { first } from 'lodash';
import { createSelector } from 'reselect';

import { RootState } from 'src/config/rootStore';
import {
    getGad2Score,
    getPhq2Score,
    getWho5Score,
} from 'src/modules/assessments/selectors/assessmentsSelectors';
import { getMyTherapists } from 'src/modules/providers/selectors/providersSelectors';
import { getSessionUtilizationInfo } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';

import moment from 'moment';
import { getFeatureFlags } from './featureFlags';

export const getUserInformation = (state: RootState) => state.user;

export const getFullName = createSelector(
    getUserInformation,
    ({ firstName, lastName }) => `${firstName} ${lastName}`,
);

export const getUserId = createSelector(getUserInformation, ({ id }) => id);

export const isUserDataFetched = createSelector(getUserInformation, ({ id }) => id > 0);

export const isInviteDependentModalVisible = createSelector(
    getUserInformation,
    ({ inviteDependentModalVisible }) => inviteDependentModalVisible,
);

export const getMobileAppUsed = createSelector(getUserInformation, user => !!user.mobileAppUsed);

export const getUserInitials = createSelector(getFullName, fullName =>
    fullName.length > 1
        ? fullName
              .split(' ')
              .map(word => word[0].toUpperCase())
              .join('')
        : '',
);

export const getAdditionalInfoProvided = createSelector(
    getUserInformation,
    ({ country, availability, languagePreferences, zipCode, city, state }) => {
        if (!country || !availability?.length || !languagePreferences?.length || !city) {
            return false;
        }

        if (country === 'United States') {
            return !!state && !!zipCode;
        }

        return true;
    },
);

export const getMostRecentCareRecommendation = createSelector(
    getUserInformation,
    ({ careRecommendations }) => first(careRecommendations),
);

// normalizes zendesk metadata category payload for 'new category' types
export const getCategorySelections = createSelector(
    getMostRecentCareRecommendation,
    mostRecentCareRec => {
        const topicSelections = mostRecentCareRec?.topicSelections ?? [];
        const categories = [...topicSelections.slice(0, 3)].map(
            ({ topicOfFocus, subtopicSelections }) => {
                return {
                    name: topicOfFocus.name,
                    subcategories: subtopicSelections.map(
                        ({ subtopicOfFocus }) => subtopicOfFocus.name,
                    ),
                    additionalInformation: '',
                };
            },
        );
        return categories;
    },
);

export const doesUserQualifyForTherapy = createSelector(
    getWho5Score,
    getSessionUtilizationInfo,
    getFeatureFlags,
    getMyTherapists,
    (who5Score, { coveredTherapyVisits }, { who5ScoreThresholdForTherapy }, myTherapists) =>
        myTherapists?.length === 0 &&
        coveredTherapyVisits > 0 &&
        who5Score > -1 &&
        who5Score < who5ScoreThresholdForTherapy,
);

export const doesUserQualifyForTherapyWithGad2Phq2 = createSelector(
    doesUserQualifyForTherapy,
    getGad2Score,
    getPhq2Score,
    (qualifiesForTherapy, gad2Score = -1, phq2Score = -1) =>
        qualifiesForTherapy && (gad2Score >= 3 || phq2Score >= 3),
);

export const getCompanyAdditionalBenefits = (state: RootState) =>
    state.user.companyAdditionalBenefits;

// used only in the onboarding
export const shouldTakeToRequestPersonalEmailAddressFlow = createSelector(
    getFeatureFlags,
    getUserInformation,
    (
        { hideRequestPersonalEmailFlowWeb },
        { email, eligiblePersonEmail, isActiveCobraRifMember },
    ) => {
        if (isActiveCobraRifMember && email === eligiblePersonEmail) {
            return true;
        }

        if (hideRequestPersonalEmailFlowWeb) {
            return false;
        }

        // we don't have information about user's work email address
        // so we can't detect if the user has already changed it or not
        if (!eligiblePersonEmail) {
            return false;
        }

        // return true only if user's email address is still the same as work one
        return eligiblePersonEmail === email;
    },
);

// will return true only if email address change is required by a company
// used outside of the onboarding (for existing users)
// For COBRA/RIF members it is a requirement to show this flow
export const shouldTakeToRequestPersonalEmailAddressFlowRequiredOnly = createSelector(
    getFeatureFlags,
    getUserInformation,
    shouldTakeToRequestPersonalEmailAddressFlow,
    (
        { workEmailAddressChangeRequired },
        { email, isActiveCobraRifMember, eligiblePersonEmail },
        shouldTake,
    ) => {
        if (isActiveCobraRifMember && email === eligiblePersonEmail) {
            return true;
        }
        return workEmailAddressChangeRequired && shouldTake;
    },
);

export const hasSignUpAfterJuneFirstSelector = (state: RootState) => {
    const joinedAt = moment(state.user.joinedAt);

    return joinedAt.isSameOrAfter('2021-06-01', 'day');
};
